import { useState, useCallback } from "react";
import { ModalType, MODAL_TYPES } from "../../constants";

interface UseModalManagementProps {
  onClearSelections: () => void;
  onClearLocationEdition: () => void;
  onClearLocationForClear: () => void;
}

interface UseModalManagementReturn {
  // State
  currentModal: ModalType;
  modalStack: ModalType[];

  // Stable functions (useCallback wrapped)
  openModal: (modalType: ModalType) => void;
  closeModal: () => void;
  isModalOpen: (modalType: ModalType) => boolean;

  // Specialized modal openers
  openSearchModal: () => void;
  openLocationPickerModal: () => void;
  openLocationContentEditionModal: () => void;
  openCraneSelectionModal: () => void;
  openMoveModal: () => void;
  openClearLocationModal: () => void;
  openInfoLocationModal: () => void;

  // Safe modal closer that handles cleanup
  safeCloseModal: () => void;
}

export const useModalManagement = ({
  onClearSelections,
  onClearLocationEdition,
  onClearLocationForClear,
}: UseModalManagementProps): UseModalManagementReturn => {
  const [currentModal, setCurrentModal] = useState<ModalType>(MODAL_TYPES.NONE);
  const [modalStack, setModalStack] = useState<ModalType[]>([]);

  // Core modal management functions with useCallback for stable references
  const openModal = useCallback((modalType: ModalType) => {
    setCurrentModal((prevModal) => {
      setModalStack((prevStack) => {
        // Add current modal to stack only if it's not NONE and not already in stack
        if (prevModal !== MODAL_TYPES.NONE && !prevStack.includes(prevModal)) {
          return [...prevStack, prevModal];
        }
        return prevStack;
      });
      return modalType;
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalStack((prevStack) => {
      if (prevStack.length > 0) {
        // Return to previous modal in stack
        const previousModal = prevStack[prevStack.length - 1];
        const newStack = prevStack.slice(0, -1);
        setCurrentModal(previousModal);
        return newStack;
      } else {
        // No modals in stack, close everything and trigger cleanup
        setCurrentModal(MODAL_TYPES.NONE);
        // Call cleanup functions
        onClearSelections();
        onClearLocationEdition();
        return [];
      }
    });
  }, [onClearSelections, onClearLocationEdition]);

  const isModalOpen = useCallback(
    (modalType: ModalType): boolean => {
      return currentModal === modalType || modalStack.includes(modalType);
    },
    [currentModal, modalStack],
  );

  // Specialized modal openers with useCallback for stable references
  const openSearchModal = useCallback(() => {
    openModal(MODAL_TYPES.SEARCH);
  }, [openModal]);

  const openLocationPickerModal = useCallback(() => {
    openModal(MODAL_TYPES.LOCATION_PICKER);
  }, [openModal]);

  const openLocationContentEditionModal = useCallback(() => {
    openModal(MODAL_TYPES.LOCATION_CONTENT_EDITION);
  }, [openModal]);

  const openCraneSelectionModal = useCallback(() => {
    openModal(MODAL_TYPES.CRANE_SELECTION);
  }, [openModal]);

  const openMoveModal = useCallback(() => {
    openModal(MODAL_TYPES.MOVE);
  }, [openModal]);

  const openClearLocationModal = useCallback(() => {
    openModal(MODAL_TYPES.CLEAR_LOCATION);
  }, [openModal]);

  const openInfoLocationModal = useCallback(() => {
    openModal(MODAL_TYPES.INFO_LOCATION);
  }, [openModal]);

  // Safe modal closer for special cases (e.g., LocationSelectionModal completion)
  const safeCloseModal = useCallback(() => {
    // Special handling for LocationSelectionModal completion
    if (
      currentModal === MODAL_TYPES.LOCATION_CONTENT_EDITION &&
      modalStack.includes(MODAL_TYPES.LOCATION_PICKER)
    ) {
      // Close LocationSelectionModal first, then clear everything
      setCurrentModal(MODAL_TYPES.NONE);
      setModalStack([]);
      onClearSelections();
      onClearLocationEdition();
      onClearLocationForClear();
    } else {
      closeModal();
    }
  }, [
    currentModal,
    modalStack,
    closeModal,
    onClearSelections,
    onClearLocationEdition,
    onClearLocationForClear,
  ]);

  return {
    // State
    currentModal,
    modalStack,

    // Stable core functions
    openModal,
    closeModal,
    isModalOpen,

    // Specialized modal openers
    openSearchModal,
    openLocationPickerModal,
    openLocationContentEditionModal,
    openCraneSelectionModal,
    openMoveModal,
    openClearLocationModal,
    openInfoLocationModal,

    // Safe closer
    safeCloseModal,
  };
};
